import React, { FC, useState, useCallback, useRef } from 'react';
import Modal from 'components/shared/Modal';
import { Button, Alert, ActivityIndicator, Icon } from '@kontentino/ui';
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons/faCloudArrowUp';
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from 'api/client';
import { CsvImportApi } from '../api/csvImport';

type Props = {
  onClose: () => void;
  pageId: number;
  socialNetwork: number;
};

const CSV_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/1JEa4ZyC6GwfvrKhBD3KKQiDvB0_gvlQUA5yw1gFAU8Q/edit?usp=sharing';

const CsvImportModal: FC<Props> = ({ onClose, pageId, socialNetwork }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const totalPostsRef = useRef<number>(0);

  const importMutation = useMutation(
    () =>
      CsvImportApi.importCsv({
        file: selectedFile!,
        pageId,
        socialNetwork,
      }),
    {
      onSuccess: (response) => {
        setIsProcessing(true);
        totalPostsRef.current = response.total_posts;
      },
      onError: (e: ApiClientError) => {
        toast(
          e.userMessage ?? t('csvImportFailed', 'Failed to import CSV file'),
          'error',
        );
      },
    },
  );

  const { data: importStatus } = useQuery(
    ['csvImportStatus'],
    () => CsvImportApi.getImportStatus(),
    {
      enabled: isProcessing,
      refetchInterval: isProcessing ? 3000 : false,
      onSuccess: (status) => {
        if (status.remaining_jobs === 0) {
          setIsProcessing(false);

          queryClient.invalidateQueries('posts');

          toast(
            t('csvImportComplete', {
              defaultValue: 'Successfully imported {{count}} posts',
              count: totalPostsRef.current,
            }),
            'success',
          );

          setTimeout(() => {
            onClose();
          }, 1500);
        }
      },
      onError: () => {
        // Continue polling even on error
      },
    },
  );

  const remainingJobs = importStatus?.remaining_jobs ?? 0;

  const handleDownloadTemplate = useCallback(() => {
    window.open(CSV_TEMPLATE_URL, '_blank');
  }, []);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        setSelectedFile(null);
        setValidationError(null);
        return;
      }

      if (!file.name.endsWith('.csv')) {
        setValidationError(t('csvInvalidFileType', 'Please select a CSV file'));
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setValidationError(null);

      setIsValidating(true);
      try {
        const validation = await CsvImportApi.validateCsvColumns(file);
        if (!validation.valid) {
          setValidationError(
            `${t(
              'csvMissingColumns',
              'CSV is missing required columns',
            )}: ${validation.missingColumns?.join(', ')}. ${t(
              'csvUseTemplate',
              'Use our template to avoid formatting issues',
            )}`,
          );
          setSelectedFile(null);
        }
      } catch (error) {
        setValidationError(
          t('csvValidationFailed', 'Failed to validate CSV file'),
        );
        setSelectedFile(null);
      } finally {
        setIsValidating(false);
      }
    },
    [t],
  );

  const isUploading = isValidating || importMutation.isLoading;
  const isLoading = isUploading || isProcessing;

  return (
    <Modal size="md" onClose={onClose} isLoading={isUploading}>
      <Modal.Header title={t('importPostsFromCsv', 'Import posts from CSV')} />

      <Modal.Content>
        <div className="tw-mx-auto tw-max-w-lg">
          <div className="tw-mb-4">
            <p className="tw-mb-3 tw-text-sm tw-text-grayscale-100">
              {t(
                'csvImportDescription',
                'Upload a CSV file to bulk import posts. All posts will be created as completed and ready for review.',
              )}
            </p>
            <p className="tw-text-xs tw-text-grayscale-100">
              <strong>{t('requiredColumns', 'Required columns')}:</strong>{' '}
              {t('date', 'Date')}, {t('time', 'Time')}, {t('text', 'Text')}
            </p>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-primary-100 tw-transition-colors hover:tw-text-primary-120"
            >
              <Icon icon={faDownload} size="sm" />
              {t('csvDownloadTemplate', 'Download CSV template')}
            </button>
          </div>

          <div className="tw-mb-4">
            <label
              className={`tw-flex tw-w-full tw-flex-col tw-items-center tw-rounded
              tw-border tw-border-dashed tw-border-primary-100
              tw-bg-white tw-p-6 tw-transition-colors
              ${
                !isLoading
                  ? 'hover:tw-cursor-pointer hover:tw-bg-primary-10'
                  : 'tw-cursor-not-allowed tw-opacity-50'
              }`}
            >
              <Icon
                icon={faCloudArrowUp}
                size="xl"
                className="tw-mb-3 tw-text-primary-100"
              />
              <div className="tw-mb-2 tw-text-center tw-text-sm tw-font-medium">
                <span className="tw-text-primary-100">
                  {t('fileUpload.clickToUpload', 'Click to upload')}
                </span>{' '}
                <span className="tw-text-grayscale-100">
                  {t('fileUpload.orDnd', 'or drag and drop')}
                </span>
              </div>
              <p className="tw-text-xs tw-text-grayscale-100">CSV</p>

              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={isLoading}
                className="tw-hidden"
              />
            </label>

            {isValidating && (
              <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2">
                <ActivityIndicator className="tw-h-4 tw-w-4" />
                <span className="tw-text-sm tw-text-grayscale-100">
                  {t('csvValidating', 'Validating CSV file...')}
                </span>
              </div>
            )}

            {validationError && (
              <Alert variant="danger" className="tw-mt-3">
                {validationError}
              </Alert>
            )}

            {selectedFile &&
              !validationError &&
              !isValidating &&
              !isProcessing && (
                <div className="tw-mt-3 tw-rounded-md tw-border tw-border-primary-100 tw-bg-primary-10 tw-p-3">
                  <p className="tw-text-sm tw-font-medium tw-text-primary-100">
                    {t('csvFileReady', 'File ready')}: {selectedFile.name}
                  </p>
                </div>
              )}

            {isProcessing && (
              <div className="tw-mt-3 tw-rounded-md tw-border tw-border-primary-100 tw-bg-primary-10 tw-p-4">
                <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
                  <ActivityIndicator className="tw-h-5 tw-w-5" />
                  <p className="tw-text-sm tw-font-medium tw-text-primary-100">
                    {remainingJobs > 0
                      ? t('csvProcessingPosts', 'Processing posts...')
                      : t('csvAlmostDone', 'Almost done...')}
                  </p>
                </div>
                <p className="tw-text-xs tw-text-grayscale-100">
                  {t('csvImportingCount', {
                    defaultValue: 'Importing {{count}} posts',
                    count: totalPostsRef.current,
                  })}
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={importMutation.isLoading}
        >
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => importMutation.mutate()}
          disabled={!selectedFile || !!validationError || isLoading}
          isLoading={importMutation.isLoading}
        >
          {t('import', 'Import')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CsvImportModal;
